import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Search, Plus, Edit2, Trash2, Eye, X, Check, CheckCircle, XCircle,
  FileText, Info
} from 'lucide-react';

const AboutAdmin = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  // Form State
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    status: true
  });

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4100";

  // Common titles to suggest to the user
  const suggestedTitles = ["Story", "Vision", "Mission", "Values"];

  // ==================== FETCH DATA ====================
  const fetchEntries = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/admin/about/get`, {
        withCredentials: true
      });
      if (Array.isArray(res.data)) {
        setEntries(res.data);
      }
    } catch (error) {
      console.error("Error fetching about entries:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  // ==================== HANDLERS ====================
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const openAddModal = () => {
    setIsEditing(false);
    setFormData({ title: "", content: "", status: true });
    setShowModal(true);
  };

  const openEditModal = (entry) => {
    setIsEditing(true);
    setFormData({
      title: entry.title,
      content: entry.content,
      status: entry.status
    });
    setSelectedEntry(entry);
    setShowModal(true);
  };

  const handleView = (entry) => {
    setSelectedEntry(entry);
    setViewModal(true);
  };

  // ==================== API ACTIONS ====================
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.content) return alert("Title and Content are required");

    try {
      let res;
      if (isEditing) {
        res = await axios.put(`${API_URL}/admin/about/update/${selectedEntry._id}`, formData, { withCredentials: true });
      } else {
        res = await axios.post(`${API_URL}/admin/about/add`, formData, { withCredentials: true });
      }

      if (res.status === 200 || res.status === 201) {
        fetchEntries();
        setShowModal(false);
        alert(isEditing ? "Section Updated!" : "Section Added!");
      }
    } catch (error) {
      console.error("Error saving section:", error);
      alert(error.response?.data?.message || "Failed to save section");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this section?")) return;

    try {
      const res = await axios.delete(`${API_URL}/admin/about/delete/${id}`, { withCredentials: true });
      if (res.status === 200) {
        fetchEntries();
      }
    } catch (error) {
      console.error("Error deleting section:", error);
      alert("Failed to delete");
    }
  };

  const handleToggleStatus = async (entry) => {
    // Assuming backend supports this or we just update the local state for now if API missing
    // Based on previous code, toggle might be stubbed or available. 
    // We'll update the boolean locally via PUT if specific toggle endpoint is missing or flaky.
    try {
      const updatedEntry = { ...entry, status: !entry.status };
      // Optimistic update
      const updatedEntries = entries.map(e => e._id === entry._id ? updatedEntry : e);
      setEntries(updatedEntries);

      await axios.put(`${API_URL}/admin/about/update/${entry._id}`, updatedEntry, { withCredentials: true });
    } catch (error) {
      console.error("Error toggling status:", error);
      fetchEntries(); // Revert
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-8 font-sans text-slate-900">

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight">About Us Content</h1>
          <p className="text-slate-500 mt-1">Manage core company information (Story, Vision, Values)</p>
        </div>
        <button
          onClick={openAddModal}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-indigo-500/20 transition-all transform hover:scale-105"
        >
          <Plus size={20} /> Add New Section
        </button>
      </div>

      {/* Content Table */}
      <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400">Loading content...</div>
        ) : entries.length === 0 ? (
          <div className="p-12 text-center text-slate-400">No content sections found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-bold">
                  <th className="p-6">Title</th>
                  <th className="p-6">Content Preview</th>
                  <th className="p-6">Status</th>
                  <th className="p-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {entries.map(entry => (
                  <tr key={entry._id} className="hover:bg-slate-50/50 transition-colors group">
                    <td className="p-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                          <Info size={20} />
                        </div>
                        <h3 className="font-bold text-slate-800 text-base">{entry.title}</h3>
                      </div>
                    </td>
                    <td className="p-6">
                      <p className="text-slate-500 text-sm line-clamp-1 max-w-md">{entry.content}</p>
                    </td>
                    <td className="p-6">
                      <button
                        onClick={() => handleToggleStatus(entry)}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 transition-colors ${entry.status
                            ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                            : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                          }`}
                      >
                        {entry.status ? <CheckCircle size={14} /> : <XCircle size={14} />}
                        {entry.status ? "Active" : "Hidden"}
                      </button>
                    </td>
                    <td className="p-6 text-right">
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => handleView(entry)} className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="View">
                          <Eye size={18} />
                        </button>
                        <button onClick={() => openEditModal(entry)} className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" title="Edit">
                          <Edit2 size={18} />
                        </button>
                        <button onClick={() => handleDelete(entry._id)} className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" title="Delete">
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ADD/EDIT MODAL */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="font-bold text-xl text-slate-800">{isEditing ? "Edit Section" : "Add New Section"}</h3>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">

              {/* Title */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Section Title</label>
                <div className="space-y-2">
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-semibold text-slate-700"
                    placeholder="e.g. Vision"
                  />
                  <div className="flex flex-wrap gap-2">
                    {suggestedTitles.map(title => (
                      <button
                        key={title}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, title }))}
                        className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg font-medium transition-colors"
                      >
                        {title}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Content */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Content</label>
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleInputChange}
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-slate-700 min-h-[150px]"
                  placeholder="Enter the detailed content for this section..."
                />
              </div>

              {/* Status */}
              <label className="flex items-center gap-3 cursor-pointer group">
                <div className={`w-12 h-7 rounded-full p-1 transition-colors duration-300 ${formData.status ? "bg-emerald-500" : "bg-slate-200"}`}>
                  <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-300 ${formData.status ? "translate-x-5" : ""}`}></div>
                </div>
                <input
                  type="checkbox"
                  name="status"
                  checked={formData.status}
                  onChange={handleInputChange}
                  className="hidden"
                />
                <span className="font-semibold text-slate-700 group-hover:text-indigo-600 transition-colors">
                  {formData.status ? "Active (Visible)" : "Hidden"}
                </span>
              </label>

              <div className="pt-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-3 bg-slate-100 text-slate-600 rounded-xl font-bold hover:bg-slate-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 shadow-lg shadow-indigo-500/20 transition-all transform hover:scale-[1.02]"
                >
                  {isEditing ? "Save Changes" : "Save Section"}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

      {/* VIEW MODAL */}
      {viewModal && selectedEntry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-8 max-w-2xl w-full shadow-2xl relative max-h-[80vh] overflow-y-auto">
            <button onClick={() => setViewModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
              <X size={24} />
            </button>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                <FileText size={24} />
              </div>
              <h3 className="text-2xl font-black text-slate-800">{selectedEntry.title}</h3>
            </div>

            <div className="prose prose-slate max-w-none">
              <p className="text-slate-600 leading-relaxed whitespace-pre-wrap text-lg">{selectedEntry.content}</p>
            </div>

            <div className="mt-8 pt-6 border-t border-slate-100 flex justify-end">
              <div className="px-4 py-2 bg-slate-100 rounded-lg inline-block text-sm font-bold text-slate-600">
                {selectedEntry.status ? "Active Content" : "Hidden Content"}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AboutAdmin;
